import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill, Modal, ConfirmDialog, MenuItem, MenuDivider } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard } from '../platform/_ui';
import { RequireCapability } from './auth/RequireCapability.jsx';
import { ORG } from './auth/capabilities.js';
import { usePermissions } from './auth/usePermissions.js';
import { useOrganization } from './OrganizationRuntimeContext.jsx';
import * as api from '../api/org-workspace-templates.api.js';

/* ── Constants ──────────────────────────────────────────────────────── */

const STATUS_OPTIONS = [
  { value: '',         label: 'All statuses'  },
  { value: 'ACTIVE',   label: 'Active'        },
  { value: 'DRAFT',    label: 'Draft'         },
  { value: 'ARCHIVED', label: 'Archived'      },
];

const SORT_OPTIONS = [
  { value: 'updatedAt', label: 'Last updated' },
  { value: 'createdAt', label: 'Date created' },
  { value: 'name',      label: 'Name'         },
];

const TIMEZONES = [
  'UTC', 'Asia/Kolkata', 'America/New_York', 'America/Los_Angeles',
  'Europe/London', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Singapore',
  'Australia/Sydney', 'America/Chicago',
];

const LOCALES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-IN', label: 'English (IN)' },
  { value: 'en-GB', label: 'English (GB)' },
  { value: 'de-DE', label: 'German'       },
  { value: 'fr-FR', label: 'French'       },
  { value: 'ja-JP', label: 'Japanese'     },
];

const MODULE_OPTIONS = [
  { value: 'People',    label: 'People',    icon: 'Users',       desc: 'Contact and person management'    },
  { value: 'Companies', label: 'Companies', icon: 'Building2',   desc: 'Company and account management'   },
  { value: 'Pipeline',  label: 'Pipeline',  icon: 'BarChart3',   desc: 'Deal stages and sales pipeline'   },
  { value: 'Tasks',     label: 'Tasks',     icon: 'CheckSquare', desc: 'Task management and workflows'    },
  { value: 'Calendar',  label: 'Calendar',  icon: 'Calendar',    desc: 'Calendar events and scheduling'   },
  { value: 'Notes',     label: 'Notes',     icon: 'StickyNote',  desc: 'Notes and annotations on records' },
];

const CHANNEL_OPTIONS = [
  { value: 'email',    label: 'Email',      icon: 'Mail'           },
  { value: 'sms',      label: 'SMS',        icon: 'Phone'          },
  { value: 'whatsapp', label: 'WhatsApp',   icon: 'MessageCircle'  },
  { value: 'chat',     label: 'Live Chat',  icon: 'MessageSquare'  },
  { value: 'voice',    label: 'Voice',      icon: 'PhoneCall'      },
  { value: 'push',     label: 'Push',       icon: 'Bell'           },
];

const DEFAULT_CONFIG = {
  workspace: { namePattern: '', timezone: 'UTC', locale: 'en-US' },
  modules:   { enabled: ['People', 'Companies', 'Pipeline', 'Tasks', 'Calendar', 'Notes'] },
  channels:  { enabled: [] },
  branding:  { inheritOrgBranding: true, inheritLogo: true, inheritTheme: true },
  security:  {
    inheritIpPolicy: true, inheritMfaPolicy: true, inheritPasswordPolicy: true,
    restrictFileUploads: false, sessionTimeoutMins: 480, idleTimeoutMins: 30,
  },
  ai:        { enabled: false, enableCopilot: false, enablePromptLibrary: false },
  developer: { enableApiAccess: false, enableOAuth: false, enableWebhooks: false, enableSandbox: false },
  storage:   { defaultAllocationGb: 10, maxFileSizeMb: 100, retentionDays: 365 },
};

/* ── Helpers ────────────────────────────────────────────────────────── */

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';

const TEMPLATE_STATUS_LABEL = { ACTIVE: 'Active', DRAFT: 'Draft', ARCHIVED: 'Archived' };

function statusTone(s) {
  if (s === 'ACTIVE')   return 'green';
  if (s === 'DRAFT')    return 'amber';
  if (s === 'ARCHIVED') return 'gray';
  return 'gray';
}

function fmtCreator(v) {
  if (!v) return '—';
  return v;
}

function mergeConfig(base, override) {
  const merged = { ...base };
  for (const key of Object.keys(override ?? {})) {
    const isPlainObj = typeof override[key] === 'object' && !Array.isArray(override[key]) && override[key] !== null;
    merged[key] = isPlainObj ? { ...base[key], ...override[key] } : override[key];
  }
  return merged;
}

function sortArrow(field, sortBy, sortOrder) {
  if (sortBy !== field) return '';
  return sortOrder === 'asc' ? ' ↑' : ' ↓';
}

/* ── Drawer ─────────────────────────────────────────────────────────── */

function TemplateDrawer({ template, onClose, onSaved }) {
  const { orgId } = useParams();
  const toast     = useToast();
  const isEdit    = Boolean(template?.id);

  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const [form, setForm] = useState(() => {
    if (template) {
      return {
        name:        template.name ?? '',
        description: template.description ?? '',
        status:      template.status ?? 'ACTIVE',
        isDefault:   template.isDefault ?? false,
        config:      mergeConfig(DEFAULT_CONFIG, template.config ?? {}),
      };
    }
    return { name: '', description: '', status: 'ACTIVE', isDefault: false, config: { ...DEFAULT_CONFIG } };
  });

  const set    = (k, v)      => setForm(f => ({ ...f, [k]: v }));
  const setCfg = (sec, k, v) => setForm(f => ({
    ...f, config: { ...f.config, [sec]: { ...f.config[sec], [k]: v } },
  }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Template name is required' });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name:        form.name.trim(),
        description: form.description.trim(),
        status:      form.status,
        isDefault:   form.isDefault,
        config:      form.config,
      };
      const saved = isEdit
        ? await api.update(orgId, template.id, payload)
        : await api.create(orgId, payload);
      const successMsg = isEdit ? 'Template updated' : 'Template created';
      toast({ tone: 'success', icon: 'Check', title: successMsg });
      onSaved(saved);
    } catch (err) {
      const detail   = err?.response?.data?.message ?? err?.message ?? 'Request failed';
      const errorMsg = isEdit ? 'Failed to update template' : 'Failed to create template';
      toast({ tone: 'error', icon: 'AlertCircle', title: errorMsg, sub: detail });
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { id: 'general',    label: 'General',            icon: 'Settings'   },
    { id: 'workspace',  label: 'Workspace Defaults',  icon: 'LayoutGrid' },
    { id: 'modules',    label: 'Modules',             icon: 'Grid'       },
    { id: 'channels',   label: 'Channels',            icon: 'Radio'      },
    { id: 'security',   label: 'Security',            icon: 'Shield'     },
    { id: 'ai',         label: 'AI Platform',         icon: 'Sparkles'   },
    { id: 'developer',  label: 'Developer',           icon: 'Code2'      },
    { id: 'branding',   label: 'Branding',            icon: 'Palette'    },
    { id: 'resources',  label: 'Resources',           icon: 'HardDrive'  },
  ];

  const cfg = form.config ?? {};

  let btnLabel = 'Create Template';
  if (saving)  btnLabel = 'Saving…';
  else if (isEdit) btnLabel = 'Save Changes';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
      <button type="button" aria-label="Close" tabIndex={-1} onClick={onClose}
        style={{ position: 'absolute', inset: 0, border: 'none', cursor: 'default', background: 'rgba(0,0,0,0.4)', width: '100%', height: '100%' }} />
      <div style={{
        position: 'relative', zIndex: 1,
        width: 640, height: '100%', background: 'var(--nx-surface)',
        boxShadow: '-4px 0 32px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: '1px solid var(--nx-border)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <NexusIcon name="Layout" size={15} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>
                {isEdit ? 'Edit Template' : 'New Template'}
              </div>
              {isEdit && (
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 1 }}>{template.name}</div>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: 4, display: 'flex', borderRadius: 4 }}>
            <NexusIcon name="X" size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--nx-border)', flexShrink: 0, paddingLeft: 24, paddingRight: 24, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
                padding: '9px 0', marginRight: 16, fontSize: 12, fontWeight: 500,
                background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
                color: activeTab === t.id ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
                borderBottom: activeTab === t.id ? '2px solid var(--nx-accent)' : '2px solid transparent',
                transition: 'color 0.1s',
              }}
            >
              <NexusIcon name={t.icon} size={12} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>

          {activeTab === 'general' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div className="nx-form-row">
                <label htmlFor="tmpl-name">Template Name <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
                <input
                  id="tmpl-name"
                  className="nx-input"
                  autoFocus
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="e.g. Standard Sales Workspace"
                  maxLength={120}
                />
              </div>
              <div className="nx-form-row">
                <label htmlFor="tmpl-desc">Description</label>
                <textarea
                  id="tmpl-desc"
                  className="nx-input"
                  style={{ height: 80, resize: 'vertical' }}
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder="Describe the purpose of this template"
                  maxLength={500}
                />
              </div>
              <div className="nx-form-row">
                <label htmlFor="tmpl-status">Status</label>
                <select id="tmpl-status" className="nx-input" value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="ACTIVE">Active</option>
                  <option value="DRAFT">Draft</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--nx-surface-alt)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
                <input
                  id="tmpl-default"
                  type="checkbox"
                  checked={form.isDefault}
                  onChange={e => set('isDefault', e.target.checked)}
                  style={{ width: 15, height: 15, flexShrink: 0 }}
                />
                <label htmlFor="tmpl-default" style={{ flex: 1, cursor: 'pointer' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>Default Template</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                    Applied automatically when no template is specified during workspace creation
                  </div>
                </label>
              </div>
            </div>
          )}

          {activeTab === 'workspace' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div className="nx-form-row">
                <label htmlFor="tmpl-ws-pattern">Workspace Name Pattern</label>
                <input
                  id="tmpl-ws-pattern"
                  className="nx-input"
                  value={cfg.workspace?.namePattern ?? ''}
                  onChange={e => setCfg('workspace', 'namePattern', e.target.value)}
                  placeholder="e.g. {org}-{team}-ws"
                />
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4 }}>
                  Use <code style={{ background: 'var(--nx-surface-alt)', padding: '1px 4px', borderRadius: 3 }}>{'{org}'}</code> and <code style={{ background: 'var(--nx-surface-alt)', padding: '1px 4px', borderRadius: 3 }}>{'{team}'}</code> as placeholders
                </div>
              </div>
              <div className="nx-form-row">
                <label htmlFor="tmpl-ws-tz">Default Timezone</label>
                <select id="tmpl-ws-tz" className="nx-input" value={cfg.workspace?.timezone ?? 'UTC'} onChange={e => setCfg('workspace', 'timezone', e.target.value)}>
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
              <div className="nx-form-row">
                <label htmlFor="tmpl-ws-locale">Default Locale</label>
                <select id="tmpl-ws-locale" className="nx-input" value={cfg.workspace?.locale ?? 'en-US'} onChange={e => setCfg('workspace', 'locale', e.target.value)}>
                  {LOCALES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
            </div>
          )}

          {activeTab === 'branding' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>
                Control how organization branding is inherited by workspaces created from this template.
              </div>
              {[
                { key: 'inheritOrgBranding', label: 'Inherit Organization Branding', desc: "Apply the organization's branding to the workspace"      },
                { key: 'inheritLogo',         label: 'Inherit Logo',                  desc: 'Use the organization logo in the workspace'              },
                { key: 'inheritTheme',        label: 'Inherit Color Theme',           desc: 'Apply the organization color scheme to the workspace'    },
              ].map(item => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--nx-surface-alt)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
                  <input
                    id={`branding-${item.key}`}
                    type="checkbox"
                    checked={cfg.branding?.[item.key] ?? true}
                    onChange={e => setCfg('branding', item.key, e.target.checked)}
                    style={{ width: 15, height: 15, flexShrink: 0 }}
                  />
                  <label htmlFor={`branding-${item.key}`} style={{ flex: 1, cursor: 'pointer' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{item.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>{item.desc}</div>
                  </label>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'modules' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>
                Select the CRM modules that are enabled by default in workspaces created from this template.
              </div>
              {MODULE_OPTIONS.map(m => {
                const enabledList = cfg.modules?.enabled ?? MODULE_OPTIONS.map(o => o.value);
                return (
                  <div key={m.value} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--nx-surface-alt)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
                    <input
                      id={`mod-${m.value}`}
                      type="checkbox"
                      checked={enabledList.includes(m.value)}
                      onChange={e => {
                        const current = cfg.modules?.enabled ?? MODULE_OPTIONS.map(o => o.value);
                        const next = e.target.checked ? [...current, m.value] : current.filter(x => x !== m.value);
                        setCfg('modules', 'enabled', next);
                      }}
                      style={{ width: 15, height: 15, flexShrink: 0 }}
                    />
                    <label htmlFor={`mod-${m.value}`} style={{ flex: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <NexusIcon name={m.icon} size={13} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{m.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>{m.desc}</div>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'channels' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>
                Define which communication channels are available in workspaces created from this template.
              </div>
              {CHANNEL_OPTIONS.map(ch => (
                <div key={ch.value} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--nx-surface-alt)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
                  <input
                    id={`ch-${ch.value}`}
                    type="checkbox"
                    checked={(cfg.channels?.enabled ?? []).includes(ch.value)}
                    onChange={e => {
                      const current = cfg.channels?.enabled ?? [];
                      const next = e.target.checked ? [...current, ch.value] : current.filter(x => x !== ch.value);
                      setCfg('channels', 'enabled', next);
                    }}
                    style={{ width: 15, height: 15, flexShrink: 0 }}
                  />
                  <label htmlFor={`ch-${ch.value}`} style={{ flex: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <NexusIcon name={ch.icon} size={13} style={{ color: 'var(--nx-text-muted)' }} />
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{ch.label}</div>
                  </label>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'security' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>
                Define how security policies are applied to workspaces created from this template.
              </div>
              {[
                { key: 'inheritIpPolicy',       label: 'Inherit IP Allowlist Policy',  desc: 'Apply the organization IP allowlist rules to the workspace'    },
                { key: 'inheritMfaPolicy',      label: 'Inherit MFA Policy',           desc: 'Enforce the organization MFA requirements in the workspace'    },
                { key: 'inheritPasswordPolicy', label: 'Inherit Password Policy',      desc: 'Apply the organization password complexity rules'             },
                { key: 'restrictFileUploads',   label: 'Restrict File Uploads',        desc: 'Block unapproved file types from being uploaded in workspaces' },
              ].map(item => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--nx-surface-alt)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
                  <input
                    id={`sec-${item.key}`}
                    type="checkbox"
                    checked={cfg.security?.[item.key] ?? item.key.startsWith('inherit')}
                    onChange={e => setCfg('security', item.key, e.target.checked)}
                    style={{ width: 15, height: 15, flexShrink: 0 }}
                  />
                  <label htmlFor={`sec-${item.key}`} style={{ flex: 1, cursor: 'pointer' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{item.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>{item.desc}</div>
                  </label>
                </div>
              ))}
              <div className="nx-form-row">
                <label htmlFor="sec-session">Session Timeout (minutes)</label>
                <input
                  id="sec-session"
                  className="nx-input"
                  type="number"
                  min={15} max={10080}
                  value={cfg.security?.sessionTimeoutMins ?? 480}
                  onChange={e => setCfg('security', 'sessionTimeoutMins', Number(e.target.value))}
                />
              </div>
              <div className="nx-form-row">
                <label htmlFor="sec-idle">Idle Timeout (minutes)</label>
                <input
                  id="sec-idle"
                  className="nx-input"
                  type="number"
                  min={5} max={480}
                  value={cfg.security?.idleTimeoutMins ?? 30}
                  onChange={e => setCfg('security', 'idleTimeoutMins', Number(e.target.value))}
                />
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>
                Configure AI platform capabilities for workspaces created from this template.
              </div>
              {[
                { key: 'enabled',             label: 'Enable AI Platform',    desc: 'Enable AI agents and knowledge base access in workspaces'         },
                { key: 'enableCopilot',       label: 'Enable AI Copilot',     desc: 'Allow AI-assisted suggestions within the workspace CRM'            },
                { key: 'enablePromptLibrary', label: 'Enable Prompt Library', desc: 'Allow agents to use organization-level prompt templates'          },
              ].map(item => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--nx-surface-alt)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
                  <input
                    id={`ai-${item.key}`}
                    type="checkbox"
                    checked={cfg.ai?.[item.key] ?? false}
                    onChange={e => setCfg('ai', item.key, e.target.checked)}
                    style={{ width: 15, height: 15, flexShrink: 0 }}
                  />
                  <label htmlFor={`ai-${item.key}`} style={{ flex: 1, cursor: 'pointer' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{item.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>{item.desc}</div>
                  </label>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'developer' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>
                Configure developer access capabilities for workspaces created from this template.
              </div>
              {[
                { key: 'enableApiAccess', label: 'Enable API Access', desc: 'Allow programmatic API access in workspaces from this template'    },
                { key: 'enableOAuth',     label: 'Enable OAuth Apps', desc: 'Allow OAuth application registration within the workspace'          },
                { key: 'enableWebhooks',  label: 'Enable Webhooks',   desc: 'Allow outbound webhook delivery in workspaces from this template' },
                { key: 'enableSandbox',   label: 'Enable Sandbox',    desc: 'Provision a sandbox environment for testing in the workspace'     },
              ].map(item => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--nx-surface-alt)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
                  <input
                    id={`dev-${item.key}`}
                    type="checkbox"
                    checked={cfg.developer?.[item.key] ?? false}
                    onChange={e => setCfg('developer', item.key, e.target.checked)}
                    style={{ width: 15, height: 15, flexShrink: 0 }}
                  />
                  <label htmlFor={`dev-${item.key}`} style={{ flex: 1, cursor: 'pointer' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{item.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>{item.desc}</div>
                  </label>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'resources' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div className="nx-form-row">
                <label htmlFor="tmpl-storage-gb">Default Storage Allocation (GB)</label>
                <input
                  id="tmpl-storage-gb"
                  className="nx-input"
                  type="number"
                  min={1} max={10000}
                  value={cfg.storage?.defaultAllocationGb ?? 10}
                  onChange={e => setCfg('storage', 'defaultAllocationGb', Number(e.target.value))}
                />
              </div>
              <div className="nx-form-row">
                <label htmlFor="tmpl-max-file">Max File Size (MB)</label>
                <input
                  id="tmpl-max-file"
                  className="nx-input"
                  type="number"
                  min={1} max={10000}
                  value={cfg.storage?.maxFileSizeMb ?? 100}
                  onChange={e => setCfg('storage', 'maxFileSizeMb', Number(e.target.value))}
                />
              </div>
              <div className="nx-form-row">
                <label htmlFor="tmpl-retention">Retention Period (days)</label>
                <input
                  id="tmpl-retention"
                  className="nx-input"
                  type="number"
                  min={1} max={3650}
                  value={cfg.storage?.retentionDays ?? 365}
                  onChange={e => setCfg('storage', 'retentionDays', Number(e.target.value))}
                />
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          gap: 8, padding: '16px 24px',
          borderTop: '1px solid var(--nx-border)', flexShrink: 0,
          background: 'var(--nx-surface)',
        }}>
          <Btn variant="ghost" icon="X" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" icon={saving ? 'Loader2' : 'Check'} disabled={saving || !form.name.trim()} onClick={handleSave}>
            {btnLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
}

/* ── Duplicate modal ────────────────────────────────────────────────── */

function DuplicateModal({ template, onClose, onDuplicated }) {
  const { orgId } = useParams();
  const toast     = useToast();
  const [name, setName]     = useState(`${template.name} (copy)`);
  const [saving, setSaving] = useState(false);

  const handleDuplicate = async () => {
    if (!name.trim()) return;
    try {
      setSaving(true);
      const copy = await api.duplicate(orgId, template.id, { name: name.trim() });
      toast({ tone: 'success', icon: 'Copy', title: 'Template duplicated' });
      onDuplicated(copy);
    } catch (err) {
      const detail = err?.response?.data?.message ?? err?.message ?? 'Request failed';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to duplicate', sub: detail });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Duplicate Template"
      icon="Copy"
      onClose={onClose}
      width={420}
      footer={<>
        <Btn variant="ghost" icon="X" onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" icon={saving ? 'Loader2' : 'Copy'} disabled={saving || !name.trim()} onClick={handleDuplicate}>
          {saving ? 'Duplicating…' : 'Duplicate'}
        </Btn>
      </>}
    >
      <div className="nx-form-row">
        <label htmlFor="dup-name">New Template Name <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
        <input
          id="dup-name"
          className="nx-input"
          autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={120}
          onKeyDown={e => { if (e.key === 'Enter') handleDuplicate(); }}
        />
      </div>
    </Modal>
  );
}

/* ── Delete confirm modal ───────────────────────────────────────────── */

function DeleteModal({ template, onClose, onDeleted }) {
  const { orgId } = useParams();
  const toast     = useToast();
  const [acting, setActing] = useState(false);

  const handleDelete = async () => {
    try {
      setActing(true);
      await api.remove(orgId, template.id);
      toast({ tone: 'warning', icon: 'Trash2', title: 'Template deleted', sub: template.name });
      onDeleted(template.id);
    } catch (err) {
      const detail = err?.response?.data?.message ?? err?.message ?? 'Request failed';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to delete', sub: detail });
      setActing(false);
    }
  };

  return (
    <ConfirmDialog
      title="Delete Template"
      desc={<>This will permanently delete <strong>{template.name}</strong>. This action cannot be undone.</>}
      danger
      busy={acting}
      confirmLabel={acting ? 'Deleting…' : 'Delete'}
      onConfirm={handleDelete}
      onCancel={onClose}
    />
  );
}

/* ── Row actions menu ───────────────────────────────────────────────── */

function ActionsMenu({ template, canEdit, canDelete, onEdit, onDuplicate, onSetDefault, onArchive, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const showSetDefault = canEdit && !template.isDefault && template.status !== 'ARCHIVED';
  const showArchive    = canEdit && !template.isDefault && template.status !== 'ARCHIVED';
  const showDelete     = canDelete && !template.isDefault;

  const close = (fn) => () => { setOpen(false); fn(); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Btn variant="ghost" size="sm" icon="MoreHorizontal"
        aria-haspopup="menu" aria-expanded={open}
        onMouseDown={e => e.preventDefault()}
        onClick={() => setOpen(v => !v)} />
      {open && (
        <div className="nx-menu" role="menu" style={{ right: 0, top: '100%', marginTop: 4 }}>
          {canEdit        && <MenuItem icon="Pencil"  onClick={close(onEdit)}>Edit</MenuItem>}
          <MenuItem icon="Copy"    onClick={close(onDuplicate)}>Duplicate</MenuItem>
          {showSetDefault && <MenuItem icon="Star"    onClick={close(onSetDefault)}>Set as Default</MenuItem>}
          {showArchive    && <MenuItem icon="Archive" onClick={close(onArchive)}>Archive</MenuItem>}
          {showDelete && (
            <>
              <MenuDivider />
              <MenuItem icon="Trash2" danger onClick={close(onDelete)}>Delete</MenuItem>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Table rows sub-component ───────────────────────────────────────── */

function TemplateRows({ templates, sortBy, sortOrder, onSortChange, canEdit, canDelete, onEdit, onDuplicate, onSetDefault, onArchive, onDelete }) {
  return (
    <div className="nx-cc-chart-card" style={{ padding: 0, overflowX: 'auto' }}>
      {/* Header */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 100px 80px 120px 140px 48px',
        padding: '10px 20px', borderBottom: '1px solid var(--nx-border)',
        fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em',
        background: 'var(--nx-surface-alt)',
      }}>
        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}
          onClick={() => onSortChange('name')}>
          Template{sortArrow('name', sortBy, sortOrder)}
        </button>
        <span>Status</span>
        <span>Default</span>
        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}
          onClick={() => onSortChange('updatedAt')}>
          Updated{sortArrow('updatedAt', sortBy, sortOrder)}
        </button>
        <span>Created By</span>
        <span />
      </div>

      {/* Rows */}
      {templates.map((t, i) => (
        <div
          key={t.id}
          className="nx-table-row"
          style={{
            display: 'grid', gridTemplateColumns: '1fr 100px 80px 120px 140px 48px',
            alignItems: 'center', padding: '14px 20px',
            borderBottom: i < templates.length - 1 ? '1px solid var(--nx-border)' : 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, flexShrink: 0,
              background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <NexusIcon name="Layout" size={15} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.name}
              </div>
              {t.description && (
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.description}
                </div>
              )}
            </div>
          </div>

          <div><Pill tone={statusTone(t.status)}>{TEMPLATE_STATUS_LABEL[t.status] ?? t.status}</Pill></div>

          <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 8 }}>
            {t.isDefault && <NexusIcon name="Star" size={14} style={{ color: 'var(--nx-accent)' }} />}
          </div>

          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{fmtDate(t.updatedAt)}</div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <NexusIcon name="User" size={11} style={{ color: 'var(--nx-text-muted)' }} />
            </div>
            <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fmtCreator(t.createdBy)}
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ActionsMenu
              template={t}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={() => onEdit(t)}
              onDuplicate={() => onDuplicate(t)}
              onSetDefault={() => onSetDefault(t)}
              onArchive={() => onArchive(t)}
              onDelete={() => onDelete(t)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Skeleton ───────────────────────────────────────────────────────── */

function TemplatesSkeleton() {
  return (
    <div className="nx-cc-chart-card" style={{ padding: 0, overflow: 'hidden' }}>
      {[0, 1, 2, 3].map(i => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: i < 3 ? '1px solid var(--nx-border)' : 'none' }}>
          <div className="nx-cc-skeleton" style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div className="nx-cc-skeleton" style={{ height: 13, width: '45%', borderRadius: 4, marginBottom: 6 }} />
            <div className="nx-cc-skeleton" style={{ height: 11, width: '60%', borderRadius: 4 }} />
          </div>
          <div className="nx-cc-skeleton" style={{ height: 22, width: 60, borderRadius: 20 }} />
          <div className="nx-cc-skeleton" style={{ height: 20, width: 80, borderRadius: 4 }} />
        </div>
      ))}
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────────────── */

export function OrgTemplatesInner() {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const toast     = useToast();
  const org       = useOrganization();
  const perm      = usePermissions();
  const canEdit   = perm.has(ORG.TEMPLATES_EDIT)   || perm.isSA;
  const canDelete = perm.has(ORG.TEMPLATES_DELETE)  || perm.isSA;

  const [templates, setTemplates]       = useState([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const [pages, setPages]               = useState(1);
  const [stats, setStats]               = useState({ total: 0, active: 0, draft: 0, defaultCount: 0 });
  const [loading, setLoading]           = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy]             = useState('updatedAt');
  const [sortOrder, setSortOrder]       = useState('desc');
  const [editTarget,      setEditTarget]      = useState(null);
  const [showCreate,      setShowCreate]      = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [deleteTarget,    setDeleteTarget]    = useState(null);
  const debounceRef  = useRef(null);
  const autoOpenRef  = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const s = await api.stats(orgId);
      setStats(s);
    } catch { /* non-blocking */ }
    finally { setStatsLoading(false); }
  }, [orgId]);

  const load = useCallback(async (p = 1) => {
    try {
      setLoading(true);
      const result = await api.list(orgId, { q: search, status: statusFilter, page: p, limit: 20, sort: sortBy, order: sortOrder });
      setTemplates(result.data ?? []);
      setTotal(result.total ?? 0);
      setPage(result.page ?? 1);
      setPages(result.pages ?? 1);
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  }, [orgId, search, statusFilter, sortBy, sortOrder, toast]);

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(1), search ? 300 : 0);
  }, [load, search]);

  useEffect(() => {
    const id = searchParams.get('templateId');
    if (!id || autoOpenRef.current === id) return;
    autoOpenRef.current = id;
    api.getById(orgId, id).then(tpl => { if (tpl) setEditTarget(tpl); }).catch(() => {});
  }, [orgId, searchParams]);

  const handleSaved = (saved) => {
    setEditTarget(null);
    setShowCreate(false);
    setTemplates(prev => {
      const idx = prev.findIndex(t => t.id === saved.id);
      if (idx >= 0) return prev.map(t => (t.id === saved.id ? saved : t));
      return [saved, ...prev];
    });
    loadStats();
  };

  const handleDuplicated = (copy) => {
    setDuplicateTarget(null);
    setTemplates(prev => [copy, ...prev]);
    loadStats();
  };

  const handleDeleted = (id) => {
    setDeleteTarget(null);
    setTemplates(prev => prev.filter(t => t.id !== id));
    setTotal(v => v - 1);
    loadStats();
  };

  const handleSetDefault = async (template) => {
    try {
      const updated = await api.setDefault(orgId, template.id);
      setTemplates(prev => prev.map(t => ({ ...t, isDefault: t.id === updated.id })));
      toast({ tone: 'success', icon: 'Star', title: `"${template.name}" set as default template` });
      loadStats();
    } catch (err) {
      const detail = err?.response?.data?.message ?? err?.message ?? 'Request failed';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to set default', sub: detail });
    }
  };

  const handleArchive = async (template) => {
    try {
      const updated = await api.archive(orgId, template.id);
      setTemplates(prev => prev.map(t => (t.id === updated.id ? updated : t)));
      toast({ tone: 'warning', icon: 'Archive', title: `"${template.name}" archived` });
      loadStats();
    } catch (err) {
      const detail = err?.response?.data?.message ?? err?.message ?? 'Request failed';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to archive', sub: detail });
    }
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(v => (v === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  if (!org) return null;

  const hasFilters = Boolean(search || statusFilter);
  const emptyTitle = hasFilters ? 'No templates match your filters' : 'No templates yet';
  const emptyDesc  = hasFilters
    ? 'Try a different keyword or clear your filters.'
    : 'Create workspace templates to define default configurations for new workspaces.';
  const totalLabel = `${total} template${total !== 1 ? 's' : ''}`;

  return (
    <>
      <PageHead
        title="Workspace Templates"
        sub={`Organization-managed blueprints for workspace provisioning in ${org.name}`}
        actions={
          <RequireCapability cap={ORG.TEMPLATES_CREATE}>
            <Btn variant="primary" icon="Plus" onClick={() => setShowCreate(true)}>New Template</Btn>
          </RequireCapability>
        }
      />

      {/* KPI cards */}
      <div className="nx-cc-stat-grid nx-kpi-4">
        <StatCard icon="Layout"      label="Total Templates" value={statsLoading ? '…' : String(stats.total)}        sub="all templates"      color="blue"   />
        <StatCard icon="CheckCircle" label="Active"          value={statsLoading ? '…' : String(stats.active)}       sub="ready to use"       color="green"  />
        <StatCard icon="FileEdit"    label="Draft"           value={statsLoading ? '…' : String(stats.draft)}        sub="in progress"        color="amber"  />
        <StatCard icon="Star"        label="Default"         value={statsLoading ? '…' : String(stats.defaultCount)} sub="applied by default" color="purple" />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <NexusIcon name="Search" size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--nx-text-muted)', pointerEvents: 'none' }} />
          <input
            className="nx-input"
            style={{ paddingLeft: 38 }}
            placeholder="Search templates…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="nx-input" style={{ width: 160 }} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); load(1); }}>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className="nx-input" style={{ width: 160 }} value={`${sortBy}:${sortOrder}`}
          onChange={e => { const [f, o] = e.target.value.split(':'); setSortBy(f); setSortOrder(o); }}>
          {SORT_OPTIONS.flatMap(o => [
            { value: `${o.value}:desc`, label: `${o.label} ↓` },
            { value: `${o.value}:asc`,  label: `${o.label} ↑` },
          ]).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {hasFilters && (
          <Btn variant="ghost" size="sm" icon="X" onClick={() => { setSearch(''); setStatusFilter(''); }}>Clear</Btn>
        )}
      </div>

      {/* Table */}
      {loading && <TemplatesSkeleton />}

      {!loading && templates.length === 0 && (
        <div className="nx-cc-chart-card" style={{ padding: '52px 24px', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <NexusIcon name="Layout" size={26} style={{ color: 'var(--nx-text-muted)' }} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 8 }}>{emptyTitle}</div>
          <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', maxWidth: 380, margin: '0 auto 20px', lineHeight: 1.6 }}>{emptyDesc}</div>
          {!hasFilters && (
            <RequireCapability cap={ORG.TEMPLATES_CREATE}>
              <Btn variant="primary" icon="Plus" onClick={() => setShowCreate(true)}>Create First Template</Btn>
            </RequireCapability>
          )}
        </div>
      )}

      {!loading && templates.length > 0 && (
        <TemplateRows
          templates={templates}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={setEditTarget}
          onDuplicate={setDuplicateTarget}
          onSetDefault={handleSetDefault}
          onArchive={handleArchive}
          onDelete={setDeleteTarget}
        />
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
            {totalLabel} · Page {page} of {pages}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <Btn variant="ghost" size="sm" icon="ChevronLeft"  disabled={page <= 1}    onClick={() => load(page - 1)}>Prev</Btn>
            <Btn variant="ghost" size="sm" icon="ChevronRight" disabled={page >= pages} onClick={() => load(page + 1)}>Next</Btn>
          </div>
        </div>
      )}

      {/* Drawers / modals */}
      {(showCreate || editTarget) && (
        <TemplateDrawer
          template={editTarget ?? null}
          onClose={() => { setShowCreate(false); setEditTarget(null); }}
          onSaved={handleSaved}
        />
      )}
      {duplicateTarget && (
        <DuplicateModal template={duplicateTarget} onClose={() => setDuplicateTarget(null)} onDuplicated={handleDuplicated} />
      )}
      {deleteTarget && (
        <DeleteModal template={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={handleDeleted} />
      )}
    </>
  );
}

const TEMPLATES_DENIED = (
  <div className="nx-empty"><NexusIcon name="Lock" size={24} /><span>You don&apos;t have permission to view workspace templates.</span></div>
);

export function OrgTemplatesEmbeddable() {
  return (
    <RequireCapability cap={ORG.TEMPLATES_VIEW} denied={TEMPLATES_DENIED}>
      <OrgTemplatesInner />
    </RequireCapability>
  );
}

export default function OrgTemplates() {
  return (
    <div className="nx-cc-page">
      <OrgTemplatesEmbeddable />
    </div>
  );
}
